import { decodeHtmlEntitiesForPlainText as plain } from '../shared/decode-html-entities';
import { rawListArray, rawListRecord, utcTimestamp, viewerTimezone } from '../shared/campaign-list-rows';
import { localDayOffset } from '../shared/campaign-session-time';
import { resolveUrl } from './catalog-list';
import type { CampaignSessionPlayerRow } from './types';

export interface CampaignRosterRow extends Record<string, unknown> { item_id: string; player_name: string | null; }
export interface CampaignRosterResponse {
    schema: 'campaign-roster-v1';
    mode: 'manage' | 'session';
    players: CampaignRosterRow[];
    scope?: Record<string, unknown>;
    viewer_id?: string;
    viewer_timezone?: string;
    server_now?: string;
    csrf_token?: string;
    invite_code?: string;
    total: number;
}
export type CampaignRosterPlayer = CampaignSessionPlayerRow & { csrf_token: string };
export interface CampaignRosterView { players: CampaignRosterPlayer[]; total: number; invite_code?: string; }

export function campaignRosterView(raw: unknown): CampaignRosterView {
    const data = raw as CampaignRosterResponse;
    if (data?.schema !== 'campaign-roster-v1' || !Array.isArray(data.players)) throw new Error('campaign-roster-schema');
    const scope = data.scope ?? {};
    const viewer = String(data.viewer_id ?? '').toUpperCase();
    const gm = data.mode === 'manage' || (viewer !== '' && viewer === String(scope.campaign_gm ?? '').toUpperCase());
    const start = utcTimestamp(scope.campaign_session_start_time);
    const now = utcTimestamp(data.server_now)?.getTime() ?? Date.now();
    const timezone = viewerTimezone(data.viewer_timezone);
    const windowOpen = start !== null && now >= localDayOffset(start, -1, timezone) && now <= localDayOffset(start, 2, timezone);
    const players = data.players.filter(row => row.item_id).map((row, index): CampaignRosterPlayer => {
        const name = plain(row.player_name), username = plain(row.player_username), owner = String(row.player_character_created_by_id ?? '');
        const controlled = viewer !== '' && [owner, String(row.permissioned ?? '')].some(id => viewer === id.toUpperCase());
        const actions = new Set(rawListArray(row.interaction_actions).map(String));
        const level = Array.from({ length: 20 }, (_, i) => Math.trunc(Number(row[`class_level_${i + 1}`]) || 0)).reduce((a, b) => a + b, 0);
        return { index: index + 1, item_id: row.item_id, character_name: name, player_username: username,
            character_url: resolveUrl(`item.php?id=${encodeURIComponent(row.item_id)}`),
            player_url: owner ? resolveUrl(`user.php?user_id=${encodeURIComponent(owner)}`) : '',
            client_creature: { player_characters: row }, client_computed: { creature: { meta: { total_level: level } } },
            thumbnail_inputs: { item_id: row.item_id, name, cdn_url: 'https://cdn.fatetide.com/', pictures: rawListArray(row.pictures).map(rawListRecord) },
            edit_url: resolveUrl(`tools/player_characters/create_player_character.php?id=${encodeURIComponent(row.item_id)}`),
            inactive: Number(row.player_character_inactive) === 1, text: `${name} ${username}`.trim().toLowerCase(),
            campaign_id: String(scope.campaign_id ?? row.attached_campaign_id ?? ''), campaign_session_id: String(scope.campaign_session_id ?? ''),
            skipping: actions.has('skipping') || actions.has('late_cancel'), late_cancel: actions.has('late_cancel'), late: actions.has('late'),
            no_show: actions.has('no_show'), left_early: actions.has('left_early'), can_manage_character: gm || controlled,
            can_manage_attendance: gm && windowOpen, can_kick: gm, csrf_token: data.csrf_token ?? '',
            // The legacy internal view type still has these unused string fields. Neither is a wire field.
            level: String(level), thumbnail_html: '', class_icons_html: '' };
    });
    return { players, total: players.length, invite_code: data.invite_code };
}
